import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";

/** Polls `/billing/status/{session_id}` after Stripe redirects back. */
export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const sponsored = params.get("sponsored") === "1";
  const [state, setState] = useState({ status: "checking", payment_status: null, test_mode: true, tier: null, period: null });
  const [error, setError] = useState("");

  useSEO({ title: "Subscription confirmed", noindex: true });

  useEffect(() => {
    if (sponsored) {
      setState({ status: "complete", payment_status: "paid", test_mode: false, tier: "family_sponsored", period: "yearly" });
      return;
    }
    if (!sessionId) {
      setError("Missing session id.");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        setState(data);
        if (data.payment_status === "paid" || data.fulfilled || data.status === "expired" || attempts >= 5) return;
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, sponsored]);

  const paid = state.payment_status === "paid";
  const expired = state.status === "expired";
  return (
    <div className="min-h-screen dotted-bg flex items-center justify-center px-5 py-10">
      <div className="brutal-card bg-white p-7 max-w-lg w-full text-center" data-testid="billing-success">
        {error ? (
          <>
            <AlertCircle size={36} className="mx-auto mb-3 text-[color:var(--coral-deep)]" />
            <h1 className="display text-2xl font-extrabold mb-2">Hmm — couldn't confirm</h1>
            <p className="text-[color:var(--ink-soft)] mb-4">{error}</p>
            <Link to="/pricing" className="pill-btn primary">Back to pricing</Link>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 size={42} className="mx-auto mb-3 text-[color:var(--coral)]" />
            <h1 className="display text-3xl font-extrabold mb-2">You're in. 🎉</h1>
            <p className="text-[color:var(--ink-soft)] mb-1">
              {sponsored ? "Sponsored pass activated — 12 months of access."
                : `${state.tier?.replace("_", " ")} · ${state.period} subscription active${state.test_mode ? " (test mode)" : ""}.`}
            </p>
            {state.test_mode && !sponsored && (
              <p className="text-xs text-[color:var(--ink-soft)] mb-4" data-testid="billing-test-mode">Test mode — no real payment was made.</p>
            )}
            <Link to="/profiles" className="pill-btn primary mt-3" data-testid="success-continue">
              Continue to boards <ArrowRight size={14} />
            </Link>
          </>
        ) : expired ? (
          <>
            <AlertCircle size={36} className="mx-auto mb-3 text-[color:var(--coral-deep)]" />
            <h1 className="display text-2xl font-extrabold mb-2">Session expired</h1>
            <p className="text-[color:var(--ink-soft)] mb-4">Try again — your kid's board is waiting.</p>
            <Link to="/pricing" className="pill-btn primary">Back to pricing</Link>
          </>
        ) : (
          <>
            <Loader2 size={36} className="mx-auto mb-3 animate-spin text-[color:var(--ink-soft)]" />
            <h1 className="display text-2xl font-extrabold mb-2">Confirming payment…</h1>
            <p className="text-[color:var(--ink-soft)]">Hang tight — Stripe is finalizing.</p>
          </>
        )}
      </div>
    </div>
  );
}
